import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import notification from "common/utils/notification"
import { GetAllImpuestosDtoProps } from "./DTOs/GetAllImpuestosDto"
import { ImpuestoEmpresaUpdateDtoProps } from "./DTOs/ImpuestoEmpresaUpdateDto"
import {
  ImpuestoEmpresaReadingDtoQueryParamsProps,
  getImpuestoEmpresa,
  getImpuestoEmpresaById,
  getImpuestoEmpresaNew,
  putImpuestoEmpresa,
} from "./ImpuestoEmpresa.service"
import { GetAllImpuestosDtoIPagedEnumerableProps } from "./Schemas/GetAllImpuestosDtoIPagedEnumerable"
import {
  SHOW_IMPUESTOS_TABLE,
  SHOW_TAXES_COMPRAS,
  TipoAsientoType,
} from "./Types/TipoAsiento"
import { TIPO_IMPUESTO_EMPRESA_ENUM } from "./Types/TipoImpuestoEmpresaEnum"

export function useImpuestoEmpresaNew(
  queryParams?: ImpuestoEmpresaReadingDtoQueryParamsProps
) {
  const queryClient = useQueryClient()
  const {
    data: impuestoEmpresa,
    isLoading: impuestoEmpresaIsLoading,
    error: impuestoEmpresaError,
  } = useQuery({
    queryKey: ["getImpuestoEmpresaNew", queryParams],
    queryFn: () => getImpuestoEmpresaNew(queryParams),
    refetchOnWindowFocus: false,
    retry: false,
  })
  const impuestoEmpresaInvalidation = () =>
    queryClient.invalidateQueries({ queryKey: ["getImpuestoEmpresaNew"] })

  return {
    impuestoEmpresa,
    impuestoEmpresaIsLoading,
    impuestoEmpresaError,
    impuestoEmpresaInvalidation,
  }
}

export function useImpuestoEmpresa() {
  const queryClient = useQueryClient()
  const {
    data: impuestoEmpresa,
    isLoading: impuestoEmpresaIsLoading,
    error: impuestoEmpresaError,
    refetch: impuestoEmpresaRefetch,
  } = useQuery({
    queryKey: ["getImpuestoEmpresa"],
    queryFn: () => getImpuestoEmpresa(),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 2, // 2 hours
    retry: false,
    retryOnMount: false,
  })
  const impuestoEmpresaInvalidation = () =>
    queryClient.invalidateQueries({ queryKey: ["getImpuestoEmpresa"] })

  const { mutateAsync: impuestoEmpresaMutation } = useMutation({
    mutationFn: async (queryBody: ImpuestoEmpresaUpdateDtoProps) =>
      putImpuestoEmpresa(queryBody).then(() => {
        impuestoEmpresaInvalidation()
      }),
  })
  const impuestoEmpresaMutationSuccess = () => notification.success()

  return {
    impuestoEmpresa,
    impuestoEmpresaIsLoading,
    impuestoEmpresaError,
    impuestoEmpresaRefetch,
    impuestoEmpresaMutationSuccess,
    impuestoEmpresaInvalidation,
    impuestoEmpresaMutation,
  }
}

export function useImpuestoEmpresaById(id?: string) {
  const queryClient = useQueryClient()
  const {
    data: impuestoEmpresaById,
    isLoading: impuestoEmpresaByIdIsLoading,
    error: impuestoEmpresaByIdError,
    refetch: impuestoEmpresaByIdRefetch,
  } = useQuery({
    queryKey: ["getImpuestoEmpresaById", id],
    queryFn: () => {
      if (!id) return Promise.resolve(null)
      return getImpuestoEmpresaById(id)
    },
    refetchOnWindowFocus: false,
    retry: false,
    retryOnMount: false,
  })
  const impuestoEmpresaByIdInvalidation = () =>
    queryClient.invalidateQueries({ queryKey: ["getImpuestoEmpresaById"] })

  const impuestoEmpresaByIdRemoval = () =>
    queryClient.removeQueries({ queryKey: ["getImpuestoEmpresaById"] })

  const { mutateAsync: impuestoEmpresaByIdMutation } = useMutation({
    mutationFn: async (queryBody: ImpuestoEmpresaUpdateDtoProps) =>
      putImpuestoEmpresa(queryBody).then(() => {
        impuestoEmpresaByIdInvalidation()
      }),
  })

  return {
    impuestoEmpresaById,
    impuestoEmpresaByIdIsLoading,
    impuestoEmpresaByIdError,
    impuestoEmpresaByIdRefetch,
    impuestoEmpresaByIdInvalidation,
    impuestoEmpresaByIdRemoval,
    impuestoEmpresaByIdMutation,
  }
}

export function useImpuestoEmpresaAsientosContables(
  tipoAsiento: TipoAsientoType
) {
  const queryClient = useQueryClient()

  const { data: impuestosEmpresa, isLoading: impuestosEmpresaIsLoading } =
    useQuery({
      queryKey: ["getImpuestoEmpresaAsientoContable", tipoAsiento],
      queryFn: async () => {
        const { items }: GetAllImpuestosDtoIPagedEnumerableProps =
          await getImpuestoEmpresaNew({
            EsActivo: true,
            TipoImpuestoEmpresa: SHOW_TAXES_COMPRAS.includes(tipoAsiento)
              ? TIPO_IMPUESTO_EMPRESA_ENUM.COMPRA
              : TIPO_IMPUESTO_EMPRESA_ENUM.VENTA,
            IsPagingEnabled: false,
          })

        const retenciones = items?.filter(
          (item: GetAllImpuestosDtoProps) =>
            item?.grupo?.categoria === "Retencion"
        )
        const iva = items?.filter(
          (item: GetAllImpuestosDtoProps) => item?.grupo?.categoria === "IVA"
        )
        const igic = items?.filter(
          (item: GetAllImpuestosDtoProps) => item?.grupo?.categoria === "IGIC"
        )

        const result = [
          {
            label: "IVA",
            items: iva,
          },
          {
            label: "Retenciones",
            items: retenciones,
          },
          {
            label: "IGIC",
            items: igic,
          },
        ]

        return result
      },
      enabled: SHOW_IMPUESTOS_TABLE.includes(tipoAsiento),
      refetchOnWindowFocus: false,
      retry: false,
    })

  const impuestoEmpresaAsientosContablesInvalidation = () =>
    queryClient.invalidateQueries({
      queryKey: ["getImpuestoEmpresaAsientoContable"],
    })

  return {
    impuestosEmpresa,
    impuestosEmpresaIsLoading,
    impuestoEmpresaAsientosContablesInvalidation,
  }
}
